import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, addDays } from 'date-fns';
import { api } from '@/lib/api';
import { formatCurrency, formatTime, cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';

export function TeeTimesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    date: searchParams.get('date') || format(new Date(), 'yyyy-MM-dd'),
    courseId: searchParams.get('courseId') || '',
    holes: searchParams.get('holes') || '',
    players: searchParams.get('players') || '',
  });

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Fetch courses
  const { data: courses } = useQuery({
    queryKey: ['courses'],
    queryFn: () => api.courses.list(),
  });

  // Fetch tee times
  const { data: teeTimes, isLoading } = useQuery({
    queryKey: ['tee-times', filters],
    queryFn: () => api.teeTimes.list({
      date: filters.date,
      courseId: filters.courseId || undefined,
      holes: filters.holes ? parseInt(filters.holes) : undefined,
      minPlayers: filters.players ? parseInt(filters.players) : undefined,
    }),
  });

  // Generate date options (next 14 days)
  const dateOptions = Array.from({ length: 14 }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      value: format(date, 'yyyy-MM-dd'),
      label: format(date, 'EEE, MMM d'),
    };
  });

  const courseOptions = [
    { value: '', label: 'All Courses' },
    ...(courses?.data?.map((c) => ({ value: c.id, label: c.name })) || []),
  ];

  const holesOptions = [
    { value: '', label: 'Any Holes' },
    { value: '9', label: '9 Holes' },
    { value: '18', label: '18 Holes' },
  ];

  const playersOptions = [
    { value: '', label: 'Any Players' },
    { value: '1', label: '1 Player' },
    { value: '2', label: '2 Players' },
    { value: '3', label: '3 Players' },
    { value: '4', label: '4 Players' },
  ];

  return (
    <div className="bg-sand-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-sand-100">
        <div className="container-wide py-8">
          <h1 className="text-3xl font-bold text-sand-900">Book a Tee Time</h1>
          <p className="mt-2 text-sand-500">Find and book available tee times at premium courses</p>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-16 z-40 bg-white border-b border-sand-100 shadow-soft">
        <div className="container-wide py-4">
          <div className="flex flex-wrap gap-3">
            <Select
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              options={dateOptions}
              className="w-40"
            />
            <Select
              value={filters.courseId}
              onChange={(e) => setFilters({ ...filters, courseId: e.target.value })}
              options={courseOptions}
              className="w-48"
            />
            <Select
              value={filters.holes}
              onChange={(e) => setFilters({ ...filters, holes: e.target.value })}
              options={holesOptions}
              className="w-32"
            />
            <Select
              value={filters.players}
              onChange={(e) => setFilters({ ...filters, players: e.target.value })}
              options={playersOptions}
              className="w-32"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="container-wide py-8">
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : teeTimes?.data?.length === 0 ? (
          <EmptyState
            icon={
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            title="No tee times available"
            description="Try adjusting your filters or selecting a different date."
          />
        ) : (
          <>
            <p className="mb-6 text-sm text-sand-500">
              {teeTimes?.data?.length} tee time{teeTimes?.data?.length !== 1 ? 's' : ''} available
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {teeTimes?.data?.map((teeTime) => (
                <Link key={teeTime.id} to={`/book/${teeTime.id}`}>
                  <Card variant="hover" className="h-full">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-sand-900">{teeTime.course?.name}</p>
                        <p className="text-sm text-sand-500">{teeTime.course?.city}, {teeTime.course?.state}</p>
                      </div>
                      <div className="flex gap-2">
                        {teeTime.isPeakTime && (
                          <Badge variant="terracotta">Peak</Badge>
                        )}
                        <Badge variant="sage">{teeTime.holes}H</Badge>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-4">
                      <div>
                        <p className="text-2xl font-bold text-sand-900">{formatTime(teeTime.time)}</p>
                        <p className="text-sm text-sand-500">{format(new Date(teeTime.date), 'MMM d, yyyy')}</p>
                      </div>
                      <div className="ml-auto text-right">
                        <p className="text-lg font-semibold text-sage-600">{formatCurrency(teeTime.basePrice)}</p>
                        <p className="text-xs text-sand-400">{teeTime.availableSpots} spot{teeTime.availableSpots !== 1 ? 's' : ''} left</p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-sm text-sand-500">
                        + {formatCurrency(teeTime.cartPrice)} cart
                      </p>
                      <Button size="sm" variant="sage">
                        Book Now
                      </Button>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
